import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Copy, Monitor, Smartphone, Globe, Plus, Trash2, ImagePlus, ExternalLink, ChevronUp, ChevronDown, Crown, Check } from "lucide-react";
import SiteRenderer from "@/components/site/SiteRenderer";
import { SITE_THEMES, SECTION_META, defaultSite } from "@/lib/siteThemes";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SiteBuilder() {
  const { wedding, hasFeature } = useAuth();
  const wid = wedding?.wedding?.id;
  const [wed, setWed] = useState(null);
  const [theme, setTheme] = useState("botanical");
  const [config, setConfig] = useState(null);
  const [published, setPublished] = useState(false);
  const [device, setDevice] = useState("desktop");
  const [editKey, setEditKey] = useState("home");
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!wid) return;
    api.get(`/weddings/${wid}/site`).then(({ data }) => {
      setWed(data.wedding);
      setTheme(data.site.theme || "botanical");
      setPublished(!!data.site.is_published);
      const def = defaultSite(data.wedding);
      const cfg = data.site.config;
      // Deep-merge stored config over defaults so a partial/legacy config never crashes the builder.
      setConfig(cfg ? {
        order: Array.isArray(cfg.order) && cfg.order.length ? cfg.order : def.order,
        sections: { ...def.sections, ...(cfg.sections || {}) },
      } : def);
    }).catch(() => setConfig(defaultSite({})));
  }, [wid]);

  const persist = useCallback((cfg, th) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put(`/weddings/${wid}/site`, { config: cfg, theme: th }).catch(() => {});
    }, 600);
  }, [wid]);

  const setSection = (key, patch) => {
    const next = { ...config, sections: { ...config.sections, [key]: { ...config.sections[key], ...patch } } };
    setConfig(next); persist(next, theme);
  };
  const pickTheme = (th) => { setTheme(th); persist(config, th); };
  const toggleSection = (key) => setSection(key, { enabled: !config.sections[key].enabled });
  const moveSection = (key, dir) => {
    const order = [...config.order]; const i = order.indexOf(key); const j = dir === "up" ? i - 1 : i + 1;
    if (j < 1 || j >= order.length - 1) return; // keep home first, rsvp last
    [order[i], order[j]] = [order[j], order[i]];
    const next = { ...config, order }; setConfig(next); persist(next, theme);
  };

  const publicUrl = `${window.location.origin}/w/${wed?.slug}`;
  const publish = async () => {
    clearTimeout(saveTimer.current);
    await api.put(`/weddings/${wid}/site`, { config, theme });
    await api.post(`/weddings/${wid}/site/publish`);
    setPublished(true); toast.success("Site publié 🎉");
  };
  const unpublish = async () => { await api.post(`/weddings/${wid}/site/unpublish`); setPublished(false); toast.success("Site repassé en brouillon"); };

  if (!config) return <Loader />;
  if (!hasFeature("wedding_site")) return <SiteDiscovery wed={wed} config={config} />;

  return (
    <div>
      <PageHeader eyebrow="Créer" title="Site de mariage"
        description="Un mini-site élégant pour vos invités : histoire, programme, lieux, RSVP."
        actions={<div className="flex items-center gap-2">
          {published && <a href={publicUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="rounded-full"><ExternalLink className="w-4 h-4 mr-1" /> Voir</Button></a>}
          <Button onClick={publish} className="rounded-full" data-testid="publish-site"><Globe className="w-4 h-4 mr-1" /> Publier</Button>
        </div>} />

      <div className="grid lg:grid-cols-[380px_1fr] gap-6 items-start">
        <div className="space-y-4">
          {/* Publication */}
          <div className="card-lux p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div><div className="font-medium text-sm">Visibilité</div>
                <div className="text-xs" style={{ color: published ? "#6a8f5f" : "#a8a29e" }}>{published ? "Publié — visible par vos invités" : "Brouillon — privé"}</div></div>
              <Switch checked={published} onCheckedChange={(v) => (v ? publish() : unpublish())} data-testid="publish-toggle" />
            </div>
            <div><Label className="text-xs">Adresse publique</Label>
              <div className="flex gap-2 mt-1"><Input value={publicUrl} readOnly className="h-9 text-xs" data-testid="site-url" />
                <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Copié"); }}><Copy className="w-4 h-4" /></Button></div></div>
          </div>

          {/* Theme */}
          <div className="card-lux p-5">
            <div className="eyebrow mb-3">Thème</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(SITE_THEMES).map(([id, th]) => (
                <button key={id} onClick={() => pickTheme(id)} data-testid={`site-theme-${id}`}
                  className={`rounded-xl overflow-hidden border text-left ${theme === id ? "ring-2 ring-champagne" : ""}`}>
                  <div className="h-12 flex items-center justify-center" style={{ background: th.bg }}>
                    <span style={{ fontFamily: th.heading, color: th.accent, fontSize: 20, fontWeight: th.weight }}>Aa</span></div>
                  <div className="px-2 py-1.5 bg-white"><div className="text-xs font-medium">{th.name}</div>
                    <div className="text-[10px] text-stone-400 leading-tight">{th.mood}</div></div>
                </button>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="card-lux p-5">
            <div className="eyebrow mb-3">Sections</div>
            <div className="space-y-1.5">
              {config.order.map((key) => {
                const meta = SECTION_META[key]; const on = config.sections[key]?.enabled;
                return (
                  <div key={key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${editKey === key ? "ring-1 ring-champagne bg-champagne/5" : ""}`}>
                    <button className="flex-1 text-left" onClick={() => setEditKey(key)} data-testid={`section-${key}`}>
                      {meta.label}{meta.fixed && <span className="text-[10px] text-stone-400 ml-1">· fixe</span>}</button>
                    {!meta.fixed && <div className="flex gap-0.5">
                      <button onClick={() => moveSection(key, "up")} className="text-stone-400 hover:text-stone-700"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveSection(key, "down")} className="text-stone-400 hover:text-stone-700"><ChevronDown className="w-3.5 h-3.5" /></button>
                    </div>}
                    <Switch checked={!!on} onCheckedChange={() => toggleSection(key)} disabled={meta.fixed} data-testid={`toggle-${key}`} className="scale-90" />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section editor */}
          <div className="card-lux p-5">
            <div className="eyebrow mb-3">{SECTION_META[editKey]?.label}</div>
            <SectionEditor wid={wid} sKey={editKey} data={config.sections[editKey]} setSection={setSection} />
          </div>
        </div>

        {/* Preview */}
        <div>
          <div className="flex items-center justify-center gap-1 mb-3">
            {[["desktop", Monitor], ["mobile", Smartphone]].map(([d, Icon]) => (
              <button key={d} onClick={() => setDevice(d)} data-testid={`site-device-${d}`}
                className={`w-9 h-9 rounded-lg flex items-center justify-center ${device === d ? "bg-stone-900 text-white" : "bg-white border"}`}><Icon className="w-4 h-4" /></button>
            ))}
          </div>
          <div className={`mx-auto card-lux overflow-hidden transition-all ${device === "mobile" ? "max-w-[390px]" : "w-full"}`} data-testid="site-preview">
            <div className="max-h-[75vh] overflow-y-auto">
              <SiteRenderer themeKey={theme} config={config} wedding={wed} device={device} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PhotoField({ wid, value, onChange, label = "Photo" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy(true);
    try { const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/weddings/${wid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND}${data.url}`);
    } catch { toast.error("Échec"); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div><Label className="text-xs">{label}</Label>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={up} />
      <div className="flex gap-2 mt-1 items-center">
        {value && <img src={value} alt="" className="w-10 h-10 rounded object-cover border" />}
        <Button variant="outline" size="sm" onClick={() => ref.current.click()} disabled={busy}><ImagePlus className="w-4 h-4 mr-1" /> {value ? "Changer" : "Téléverser"}</Button>
        {value && <button onClick={() => onChange("")} className="text-stone-400 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>}
      </div></div>
  );
}

const FIELD_SETS = {
  schedule: [["time", "Heure"], ["title", "Intitulé"], ["location", "Lieu"], ["desc", "Détail"]],
  locations: [["name", "Nom"], ["address", "Adresse"], ["desc", "Description"], ["mapUrl", "Lien carte"]],
  accommodations: [["name", "Nom"], ["desc", "Description"], ["url", "Lien réservation"], ["note", "Note"]],
  faq: [["q", "Question"], ["a", "Réponse"]],
  practical: [["title", "Titre"], ["text", "Texte"]],
};

function SectionEditor({ wid, sKey, data, setSection }) {
  if (!data) return null;
  const titleField = data.title !== undefined && (
    <div><Label className="text-xs">Titre de la section</Label>
      <Input value={data.title} onChange={(e) => setSection(sKey, { title: e.target.value })} className="mt-1 h-9" data-testid={`edit-${sKey}-title`} /></div>
  );

  if (sKey === "home")
    return <div className="space-y-3">
      <div><Label className="text-xs">Suptitre</Label><Input value={data.eyebrow} onChange={(e) => setSection(sKey, { eyebrow: e.target.value })} className="mt-1 h-9" /></div>
      <div><Label className="text-xs">Titre (vos prénoms)</Label><Input value={data.title} onChange={(e) => setSection(sKey, { title: e.target.value })} className="mt-1 h-9" data-testid="edit-home-title" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Date</Label><Input type="date" value={data.date || ""} onChange={(e) => setSection(sKey, { date: e.target.value })} className="mt-1 h-9" /></div>
        <div><Label className="text-xs">Ville</Label><Input value={data.city} onChange={(e) => setSection(sKey, { city: e.target.value })} className="mt-1 h-9" /></div>
      </div>
      <PhotoField wid={wid} value={data.photo} onChange={(v) => setSection(sKey, { photo: v })} label="Photo d'en-tête" />
    </div>;

  if (sKey === "story")
    return <div className="space-y-3">{titleField}
      <div><Label className="text-xs">Votre histoire</Label><Textarea value={data.text} onChange={(e) => setSection(sKey, { text: e.target.value })} rows={5} className="mt-1" data-testid="edit-story-text" /></div>
      <PhotoField wid={wid} value={data.photo} onChange={(v) => setSection(sKey, { photo: v })} />
    </div>;

  if (sKey === "gallery")
    return <div className="space-y-3">{titleField}
      <div className="grid grid-cols-3 gap-2">{(data.photos || []).map((p, i) => (
        <div key={i} className="relative"><img src={p} alt="" className="w-full aspect-square object-cover rounded border" />
          <button onClick={() => setSection(sKey, { photos: data.photos.filter((_, x) => x !== i) })} className="absolute top-1 right-1 w-5 h-5 rounded-full bg-white/90 border flex items-center justify-center"><Trash2 className="w-3 h-3" /></button></div>))}</div>
      <PhotoField wid={wid} value="" onChange={(v) => setSection(sKey, { photos: [...(data.photos || []), v] })} label="Ajouter une photo" />
    </div>;

  if (sKey === "rsvp")
    return <div className="space-y-3">{titleField}
      <div><Label className="text-xs">Message</Label><Textarea value={data.text} onChange={(e) => setSection(sKey, { text: e.target.value })} rows={3} className="mt-1" /></div>
      <div><Label className="text-xs">Date limite de réponse</Label><Input type="date" value={data.deadline || ""} onChange={(e) => setSection(sKey, { deadline: e.target.value })} className="mt-1 h-9" /></div>
    </div>;

  // list-based sections
  const listKey = sKey === "practical" ? "blocks" : "items";
  const fields = FIELD_SETS[sKey] || [];
  const items = data[listKey] || [];
  const upItem = (i, k, v) => setSection(sKey, { [listKey]: items.map((it, x) => (x === i ? { ...it, [k]: v } : it)) });
  const addItem = () => setSection(sKey, { [listKey]: [...items, Object.fromEntries(fields.map(([k]) => [k, ""]))] });
  const delItem = (i) => setSection(sKey, { [listKey]: items.filter((_, x) => x !== i) });

  return <div className="space-y-3">{titleField}
    {items.map((it, i) => (
      <div key={i} className="p-3 rounded-lg border space-y-2 relative">
        <button onClick={() => delItem(i)} className="absolute top-2 right-2 text-stone-400 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
        {fields.map(([k, lbl]) => (
          k === "desc" || k === "text" || k === "a"
            ? <div key={k}><Label className="text-[11px]">{lbl}</Label><Textarea value={it[k] || ""} onChange={(e) => upItem(i, k, e.target.value)} rows={2} className="mt-0.5 text-sm" /></div>
            : <div key={k}><Label className="text-[11px]">{lbl}</Label><Input value={it[k] || ""} onChange={(e) => upItem(i, k, e.target.value)} className="mt-0.5 h-8 text-sm" /></div>
        ))}
      </div>
    ))}
    <Button variant="outline" size="sm" className="w-full" onClick={addItem} data-testid={`add-${sKey}-item`}><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
  </div>;
}

function SiteDiscovery({ wed, config }) {
  const navigate = useNavigate();
  const [th, setTh] = useState("botanical");
  return (
    <div>
      <PageHeader eyebrow="Créer · Premium" title="Site de mariage"
        description="Découvrez le créateur de site de mariage. Passez Premium pour créer et publier le vôtre." />
      <div className="rounded-2xl p-6 sm:p-8 mb-8" style={{ background: "linear-gradient(120deg,#1c1b1a,#3a332a)" }} data-testid="site-premium-cta">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-1 text-champagne text-xs uppercase tracking-wide mb-2"><Crown className="w-3.5 h-3.5" /> Fonction Premium</div>
            <div className="font-serif text-2xl text-white">Créez votre site de mariage avec Premium</div>
            <p className="text-white/70 text-sm mt-2">7 thèmes élégants, sections modulaires (histoire, programme, lieux, RSVP…), aperçu en direct et publication sur votre adresse personnalisée.</p>
          </div>
          <Button onClick={() => navigate("/app/premium")} data-testid="site-upgrade-btn"
            className="rounded-full bg-champagne text-stone-900 hover:bg-champagne/90 shrink-0">Débloquer mon site</Button>
        </div>
      </div>
      <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
        <div className="card-lux p-5">
          <div className="eyebrow mb-3">Thèmes disponibles</div>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(SITE_THEMES).map(([id, t]) => (
              <button key={id} onClick={() => setTh(id)} data-testid={`disc-theme-${id}`}
                className={`rounded-xl overflow-hidden border text-left ${th === id ? "ring-2 ring-champagne" : ""}`}>
                <div className="h-12 flex items-center justify-center" style={{ background: t.bg }}>
                  <span style={{ fontFamily: t.heading, color: t.accent, fontSize: 20, fontWeight: t.weight }}>Aa</span></div>
                <div className="px-2 py-1.5 bg-white"><div className="text-xs font-medium">{t.name}</div>
                  <div className="text-[10px] text-stone-400 leading-tight">{t.mood}</div></div>
              </button>
            ))}
          </div>
        </div>
        <div className="card-lux overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto pointer-events-none">
            <SiteRenderer themeKey={th} config={config} wedding={wed} device="desktop" />
          </div>
        </div>
      </div>
    </div>
  );
}
